import React, { useMemo } from 'react';
import { Box, SimpleGrid, Flex, Text, Icon, VStack } from '@chakra-ui/react';
import { FiFolder, FiClock, FiInbox, FiChevronRight } from 'react-icons/fi';
import { casosUtils } from 'services/casosService';

const shortDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' });
};

const RecentCard = ({ item, onOpen }) => {
  const pastel = item.pastel || 'sky';
  return (
    <Flex as="button" onClick={() => onOpen(item)} align="center" gap="10px" textAlign="left"
      bg="white" border="1px solid" borderColor="cream.line" borderRadius="12px" p="9px 12px"
      transition=".13s" _hover={{ borderColor: 'coral.500', bg: 'coral.tint' }}>
      <Flex w="32px" h="32px" borderRadius="9px" bg={`${pastel}.bg`} color={`${pastel}.ink`}
        align="center" justify="center" flexShrink={0}>
        <Icon as={item.icon || FiFolder} boxSize="14px" />
      </Flex>
      <Box flex={1} minW={0}>
        <Text fontWeight="700" fontSize="12.5px" color="ink.900" noOfLines={1}>
          {item.survey?.name || 'Sin título'}
        </Text>
        <Text fontSize="11px" color="ink.400" fontWeight="600">
          {casosUtils.getTimeAgo(item.created_at)} · #{item.id}
        </Text>
      </Box>
    </Flex>
  );
};

const FolderCard = ({ category, lastDate, onOpen }) => {
  const isEmpty = category.count === 0;
  const pastel = category.pastel || 'sky';
  return (
    <Flex as="button" onClick={() => onOpen(category.slug)} direction="column" textAlign="left"
      bg="white" border="1px solid" borderColor="cream.line" borderRadius="16px" p="18px"
      opacity={isEmpty ? 0.65 : 1} transition=".15s"
      _hover={{ borderColor: 'coral.500', transform: 'translateY(-2px)', boxShadow: '0 8px 18px -10px rgba(54,38,22,.18)' }}>
      <Flex align="flex-start" justify="space-between" mb="18px">
        <Flex w="46px" h="46px" borderRadius="13px" bg={`${pastel}.bg`} color={`${pastel}.ink`}
          align="center" justify="center">
          <Icon as={category.icon || FiFolder} boxSize="21px" />
        </Flex>
        <Box as="span" px="9px" py="3px" borderRadius="999px" bg="cream.bg2" color="ink.700"
          fontSize="11.5px" fontWeight="800">
          {category.count} {category.count === 1 ? 'ficha' : 'fichas'}
        </Box>
      </Flex>
      <Text fontWeight="800" fontSize="15px" color={isEmpty ? 'ink.400' : 'ink.900'} letterSpacing="-0.01em">
        {category.displayName}
      </Text>
      <Text fontSize="12px" color="ink.400" fontWeight="500" mt="5px" lineHeight="1.4">
        {isEmpty ? 'Sin fichas registradas' : <>Última: <Text as="span" fontWeight="700" color="ink.700">{shortDate(lastDate)}</Text></>}
      </Text>
    </Flex>
  );
};

const FichasDriveView = ({ categories = [], surveys = [], onOpenFolder, onOpenFicha }) => {
  const byDateDesc = useMemo(
    () => [...surveys].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [surveys]
  );

  const categoryMeta = useMemo(() => {
    const map = {};
    categories.forEach(c => { map[c.slug] = c; });
    return map;
  }, [categories]);

  const lastDateByCategory = useMemo(() => {
    const map = {};
    surveys.forEach(item => {
      const slug = item.survey?.category || 'otros';
      const created = item.created_at;
      if (!map[slug] || new Date(created) > new Date(map[slug])) map[slug] = created;
    });
    return map;
  }, [surveys]);

  const withPastel = (item) => ({ ...item, pastel: categoryMeta[item.survey?.category || 'otros']?.pastel, icon: categoryMeta[item.survey?.category || 'otros']?.icon });

  const recent = byDateDesc.slice(0, 4).map(withPastel);
  // Evita repetir en la tabla las mismas fichas que ya se muestran arriba en "Fichas recientes"
  const recentTable = byDateDesc.slice(recent.length, recent.length + 8).map(withPastel);

  if (surveys.length === 0 && categories.every(c => c.count === 0)) {
    return (
      <Box bg="cream.bg2" borderRadius="16px" border="1.5px dashed" borderColor="cream.line2" p={10} textAlign="center">
        <Icon as={FiInbox} boxSize={9} color="ink.400" mb={3} />
        <Text fontSize="13.5px" color="ink.700" fontWeight="700">No hay fichas registradas</Text>
        <Text fontSize="12.5px" color="ink.400" mt={1}>Las fichas completadas aparecerán organizadas por categoría aquí</Text>
      </Box>
    );
  }

  return (
    <Box fontFamily="jakarta">
      {recent.length > 0 && (
        <>
          <Text fontSize="13px" fontWeight="800" color="ink.900" mb="12px">Fichas recientes</Text>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="10px" mb="28px">
            {recent.map(item => <RecentCard key={item.id} item={item} onOpen={onOpenFicha} />)}
          </SimpleGrid>
        </>
      )}

      <Text fontSize="13px" fontWeight="800" color="ink.900" mb="12px">Carpetas por categoría</Text>
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing="14px" mb="28px">
        {categories.map(category => (
          <FolderCard key={category.slug} category={category} lastDate={lastDateByCategory[category.slug]} onOpen={onOpenFolder} />
        ))}
      </SimpleGrid>

      {recentTable.length > 0 && (
        <>
          <Flex align="center" gap="8px" mb="12px">
            <Icon as={FiClock} boxSize="15px" color="ink.400" />
            <Text fontSize="13px" fontWeight="800" color="ink.900">Últimas ingresadas</Text>
          </Flex>
          <Box bg="white" border="1px solid" borderColor="cream.line" borderRadius="16px" overflow="hidden">
            <VStack spacing="0" align="stretch" divider={<Box borderTop="1px solid" borderColor="cream.line" />}>
              {recentTable.map(item => (
                <Flex key={item.id} as="button" onClick={() => onOpenFicha(item)} align="center" gap="14px"
                  p="13px 18px" _hover={{ bg: 'coral.tint' }} transition=".13s">
                  <Flex w="36px" h="36px" borderRadius="10px" flexShrink={0}
                    bg={`${item.pastel || 'sky'}.bg`} color={`${item.pastel || 'sky'}.ink`} align="center" justify="center">
                    <Icon as={item.icon || FiFolder} boxSize="16px" />
                  </Flex>
                  <Box flex={1} minW={0} textAlign="left">
                    <Text fontSize="13.5px" fontWeight="700" color="ink.900" noOfLines={1}>
                      {item.survey?.name || 'Sin título'}
                    </Text>
                    <Text fontSize="11.5px" color="ink.400" fontWeight="500">#{item.id}</Text>
                  </Box>
                  <Text fontSize="12px" color="ink.400" fontWeight="600" flexShrink={0}>
                    {casosUtils.getTimeAgo(item.created_at)}
                  </Text>
                  <Icon as={FiChevronRight} boxSize="14px" color="ink.400" flexShrink={0} />
                </Flex>
              ))}
            </VStack>
          </Box>
        </>
      )}
    </Box>
  );
};

export default FichasDriveView;
